import { useState } from 'react';
import { Box, Button, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CreditLimitDialog from './CreditLimitDialog';
import ConfirmDialog from '../common/ConfirmDialog';
import licenseComplianceService from '../../services/licenseComplianceService';
import type {
  CreditLimitDto,
  LicenseSetupEntryDto,
  UpsertCreditLimitDto,
} from '../../services/licenseComplianceService';
import { getApiErrorInfo } from '../../services/api';
import { formatCreditLimitBasis } from '../../utils/creditLimitBasis';

interface CreditLimitsListProps {
  entry: LicenseSetupEntryDto;
  /** Called after any successful mutation so the parent can refresh state. */
  onSaved: () => void | Promise<void>;
}

function formatCap(limit: Pick<CreditLimitDto, 'maxHours' | 'maxPercent'>): string {
  if (limit.maxHours != null && limit.maxPercent != null) {
    return `Max ${limit.maxHours} hrs or ${limit.maxPercent}% of total (stricter applies)`;
  }
  if (limit.maxPercent != null) {
    return `Max ${limit.maxPercent}% of total`;
  }
  return `Max ${limit.maxHours} hrs`;
}

function formatScope(scope: CreditLimitDto['scope']): string {
  return scope === 'per_year' ? 'each year' : 'entire period';
}

/**
 * Credit limits (delivery-method or field-of-study caps) for one license:
 * tracked list with edit/delete and an add button. Hours beyond a cap don't
 * count toward the total requirement — informational, never a violation.
 */
export default function CreditLimitsList({ entry, onSaved }: CreditLimitsListProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLimit, setEditingLimit] = useState<CreditLimitDto | null>(null);
  const [deletingLimit, setDeletingLimit] = useState<CreditLimitDto | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function handleSubmit(dto: UpsertCreditLimitDto) {
    try {
      if (editingLimit) {
        await licenseComplianceService.updateCreditLimit(entry.licenseId, editingLimit.id, dto);
      } else {
        await licenseComplianceService.addCreditLimit(entry.licenseId, dto);
      }
    } catch (error) {
      // Re-throw with a friendly message for the dialog to display
      throw new Error(getApiErrorInfo(error).message, { cause: error });
    }
    await onSaved();
  }

  async function handleDelete() {
    if (!deletingLimit) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await licenseComplianceService.deleteCreditLimit(entry.licenseId, deletingLimit.id);
      setDeletingLimit(null);
      await onSaved();
    } catch (error) {
      setDeleteError(getApiErrorInfo(error).message);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
        Credit limits
      </Typography>

      {entry.creditLimits.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          No credit limits are being tracked for this license. Some states cap how many
          hours can come from certain delivery methods (e.g. self-study) or fields of
          study (e.g. personal development).
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5 }}>
          {entry.creditLimits.map((limit) => (
            <Box
              key={limit.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                px: 1.5,
                py: 1,
              }}
            >
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {limit.name}
                  </Typography>
                  {limit.source === 'state_prefill' ? (
                    <Chip label="State default" size="small" variant="outlined" />
                  ) : (
                    <Chip
                      label="Custom"
                      size="small"
                      sx={{ bgcolor: 'status.modified.bg', color: 'status.modified.fg' }}
                    />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {`${formatCap(limit)} · ${formatScope(limit.scope)} · ${formatCreditLimitBasis(limit)}`}
                </Typography>
              </Box>
              <Tooltip title="Edit limit">
                <IconButton
                  size="small"
                  aria-label={`Edit ${limit.name}`}
                  onClick={() => {
                    setEditingLimit(limit);
                    setDialogOpen(true);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete limit">
                <IconButton
                  size="small"
                  aria-label={`Delete ${limit.name}`}
                  onClick={() => {
                    setDeleteError(null);
                    setDeletingLimit(limit);
                  }}
                  sx={{ '&:hover': { color: 'error.main' } }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Box>
      )}

      <Button
        size="small"
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={() => {
          setEditingLimit(null);
          setDialogOpen(true);
        }}
      >
        Add credit limit
      </Button>

      <CreditLimitDialog
        open={dialogOpen}
        limit={editingLimit}
        onSubmit={handleSubmit}
        onClose={() => setDialogOpen(false)}
      />

      <ConfirmDialog
        open={deletingLimit !== null}
        title="Delete credit limit?"
        message={`"${deletingLimit?.name ?? ''}" will no longer cap hours for this license. Credits you've earned are not affected.`}
        loading={deleting}
        error={deleteError}
        onConfirm={handleDelete}
        onClose={() => setDeletingLimit(null)}
      />
    </Box>
  );
}
